import { Box, Button, CircularProgress } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { Text } from "@src/appV2/redesign/components/Text";
import { berlinRebrandTheme } from "@src/appV2/redesign/theme/theme";

const clipboardWordmark = `${import.meta.env.BASE_URL}assets/clipboard-wordmark-brown.png`;

interface MobileAuthLoadingProps {
  error?: string;
  onRetry?: () => void;
}

/**
 * Full-screen loading/error screen shown inside the native Capacitor app
 * while Cognito authentication is in progress after the user taps "Open"
 * from the email link.
 */
export function MobileAuthLoading({ error, onRetry }: MobileAuthLoadingProps) {
  return (
    <ThemeProvider theme={berlinRebrandTheme}>
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "background.baseDark",
          overflow: "auto",
          paddingX: 5,
          paddingTop: "max(24px, var(--safe-area-inset-top))",
          paddingBottom: "max(24px, var(--safe-area-inset-bottom))",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            margin: "auto",
            gap: 7,
          }}
        >
          {/* Wordmark */}
          <img src={clipboardWordmark} alt="Clipboard" style={{ height: 70, width: "auto" }} />

          {!error && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 7,
              }}
            >
              <CircularProgress size={48} sx={{ color: "primary.main" }} />
              <Text bold variant="body1" sx={{ color: "base.base" }}>
                Signing you in...
              </Text>
            </Box>
          )}

          {error && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Text bold variant="h5" sx={{ color: "error.main", textAlign: "center" }}>
                Sign in failed
              </Text>
              <Text bold variant="body1" sx={{ color: "base.muted", textAlign: "center" }}>
                {error}
              </Text>
              {onRetry && (
                <Button
                  variant="text"
                  sx={{
                    textTransform: "none",
                    fontSize: "0.875rem",
                    color: "primary.main",
                  }}
                  onClick={onRetry}
                >
                  Back to login
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
}
